import {SyncStatus} from './entity.js';

export const SYNC_RETRY_BASE_DELAY_MS=1000;
export const SYNC_RETRY_MAX_DELAY_MS=60_000;

export function syncRetryDelayMs(attempts,{baseMs=SYNC_RETRY_BASE_DELAY_MS,maxMs=SYNC_RETRY_MAX_DELAY_MS}={}){
  const count=Math.max(1,Number.parseInt(attempts,10)||1);
  const base=Number(baseMs);
  const max=Number(maxMs);
  if(!Number.isFinite(base)||base<1)throw new TypeError('baseMs must be positive');
  if(!Number.isFinite(max)||max<base)throw new TypeError('maxMs must be >= baseMs');
  return Math.min(max,base*(2**Math.min(count-1,20)));
}

function pendingOrder(left,right){
  const a=String(left?.updatedAt||left?.createdAt||'');
  const b=String(right?.updatedAt||right?.createdAt||'');
  if(a!==b)return a.localeCompare(b);
  return String(left?.id||'').localeCompare(String(right?.id||''));
}

function retryTimestamp(failedAt,attempts){
  const time=Date.parse(String(failedAt||''));
  if(Number.isNaN(time))throw new TypeError('sync failure timestamp must be valid');
  return new Date(time+syncRetryDelayMs(attempts)).toISOString();
}

export function createSyncQueue({repository,now=()=>new Date().toISOString()}={}){
  if(!repository?.put||!repository?.listPending)throw new TypeError('repository with put/listPending is required');

  async function enqueue(entityType,entity,operation='update'){
    if(!entity?.id)throw new TypeError('entity.id is required');
    if(!['create','update','delete'].includes(operation))throw new TypeError(`Unsupported sync operation: ${operation}`);
    const timestamp=now();
    const status=operation==='create'
      ?SyncStatus.PENDING_CREATE
      :operation==='delete'
        ?SyncStatus.PENDING_DELETE
        :entity.syncStatus===SyncStatus.PENDING_CREATE
          ?SyncStatus.PENDING_CREATE
          :SyncStatus.PENDING_UPDATE;
    const next={
      ...entity,
      updatedAt:timestamp,
      deletedAt:operation==='delete'?timestamp:entity.deletedAt||null,
      syncStatus:status,
      syncAttempts:Number(entity.syncAttempts)||0,
      lastSyncAttemptAt:entity.lastSyncAttemptAt||null,
      lastSyncFailureAt:null,
      lastSyncErrorCode:null,
      nextSyncAttemptAt:null
    };
    await repository.put(entityType,next);
    return next;
  }

  async function markAttempt(entityType,entity){
    if(!entity?.id)throw new TypeError('entity.id is required');
    const next={
      ...entity,
      syncAttempts:(Number(entity.syncAttempts)||0)+1,
      lastSyncAttemptAt:now(),
      nextSyncAttemptAt:null
    };
    await repository.put(entityType,next);
    return next;
  }

  async function markFailure(entityType,entity,errorCode='SYNC_FAILED'){
    if(!entity?.id)throw new TypeError('entity.id is required');
    const failedAt=now();
    const next={
      ...entity,
      lastSyncFailureAt:failedAt,
      lastSyncErrorCode:String(errorCode||'SYNC_FAILED').slice(0,80),
      nextSyncAttemptAt:retryTimestamp(failedAt,entity.syncAttempts)
    };
    await repository.put(entityType,next);
    return next;
  }

  async function markConflict(entityType,entity,conflict){
    const next={
      ...entity,
      syncStatus:SyncStatus.CONFLICT,
      conflict:{
        detectedAt:now(),
        localServerVersion:Number(entity?.serverVersion)||0,
        ...(conflict&&typeof conflict==='object'?conflict:{reason:String(conflict||'unknown')})
      },
      updatedAt:now(),
      nextSyncAttemptAt:null
    };
    await repository.put(entityType,next);
    return next;
  }

  async function listPending(){
    const rows=await repository.listPending();
    return [...(rows||[])].sort(pendingOrder);
  }

  async function listReady({at=now()}={}){
    const atMillis=Date.parse(String(at||''));
    if(Number.isNaN(atMillis))throw new TypeError('retry evaluation timestamp must be valid');
    const rows=await listPending();
    return rows.filter(row=>{
      if(!row?.nextSyncAttemptAt)return true;
      const next=Date.parse(String(row.nextSyncAttemptAt));
      return Number.isNaN(next)||next<=atMillis;
    });
  }

  return Object.freeze({enqueue,markAttempt,markFailure,markConflict,listPending,listReady});
}
